import React, { useState } from 'react';
import { Download, RefreshCw, Shield, Database, FileText, FileSpreadsheet, FileJson, File, Calendar, CheckSquare } from 'lucide-react';
import { toast } from 'react-hot-toast';
import profileDataService from '../../../services/profileDataService';
import timelineService from '../../../services/timelineService';

const ExportManagementTab = ({ profileData, completeness, inconsistencies, onVerifyData }) => {
  const [exporting, setExporting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const handleExport = async (format) => {
    try {
      setExporting(true);
      
      let data;
      let filename;
      let mimeType;
      
      switch (format) {
        case 'academic':
          data = profileDataService.exportProfile(profileData, 'text');
          filename = 'academic_profile.txt';
          mimeType = 'text/plain';
          break;
          
        case 'assessment':
          // For PDF, we'd need a PDF library - for now export as formatted text
          data = JSON.stringify(profileData.assessment, null, 2);
          filename = 'assessment_results.json';
          mimeType = 'application/json';
          break;
          
        case 'colleges':
          // For Excel, we'd need a library - for now export as CSV
          data = profileDataService.exportProfile({ colleges: profileData.colleges }, 'csv');
          filename = 'college_list.csv';
          mimeType = 'text/csv';
          break;
          
        case 'complete':
          data = profileDataService.exportProfile(profileData, 'json');
          filename = 'complete_profile.json';
          mimeType = 'application/json';
          break;
          
        case 'summary':
          data = profileData.academic?.ai_strategic_summary || 'No strategic summary available';
          filename = 'strategic_summary.txt';
          mimeType = 'text/plain';
          break;

        case 'timeline':
          // Get user ID from profile data
          const userId = profileData?.academic?.user_id || profileData?.user_id || profileData?.id;
          if (!userId) {
            throw new Error('User ID not found in profile data');
          }

          // Fetch all timeline items
          const timelineResult = await timelineService.getTimelineItems(userId);
          if (timelineResult.error) {
            throw new Error('Failed to fetch timeline items: ' + timelineResult.error.message);
          }

          const timelineItems = timelineResult.data || [];

          // Format as comprehensive CSV with all relevant fields
          const headers = [
            'ID', 'Date', 'Due Date', 'Title', 'Description', 'Status', 'Category', 'Priority',
            'College', 'App Round', 'Source Type', 'Source ID', 'Milestone ID',
            'Estimated Hours', 'Estimated Duration', 'Actual Minutes', 'Percent Complete',
            'Importance', 'Cognitive Intensity', 'Timing Sensitivity', 'Is Redistributable',
            'Is User Created', 'Is Subtask', 'Is Batch', 'Parent Task ID', 'Epic ID', 'Epic Sequence',
            'Workflow Phase', 'Pending Reason', 'Blocked By', 'Profile Section', 'Essay Type',
            'Prerequisites', 'Dependencies', 'Tags', 'Resource URLs', 'Module Name', 'Module Link',
            'Started At', 'Completed Date', 'Status Updated At', 'Notes', 'Created At', 'Updated At'
          ];

          const csvRows = [headers.join(',')];
          timelineItems.forEach(item => {
            // Helper function to safely stringify and escape JSON fields
            const jsonToString = (obj) => {
              if (!obj) return '';
              try {
                return `"${JSON.stringify(obj).replace(/"/g, '""')}"`;
              } catch (e) {
                return '""';
              }
            };

            // Helper function to escape CSV fields
            const escapeField = (field) => {
              if (field === null || field === undefined) return '';
              const str = String(field);
              return str.includes(',') || str.includes('"') || str.includes('\n')
                ? `"${str.replace(/"/g, '""')}"`
                : str;
            };

            const row = [
              item.id || '',
              item.scheduled_date || '',
              item.due_date || '',
              escapeField(item.title || ''),
              escapeField(item.description || ''),
              item.status || '',
              item.category || '',
              item.priority || '',
              escapeField(item.college?.name || item.college_name || ''),
              item.application_round || '',
              item.source_type || '',
              item.source_id || '',
              item.milestone_id || '',
              item.estimated_hours || '',
              item.estimated_duration || '',
              item.estimated_minutes || '',
              item.percent_complete || 0,
              item.importance_level || '',
              item.cognitive_intensity || '',
              item.timing_sensitivity || '',
              item.is_redistributable !== null ? item.is_redistributable : true,
              item.is_user_created || false,
              item.is_subtask || false,
              item.is_batch || false,
              item.parent_task_id || '',
              item.epic_id || '',
              item.epic_sequence || '',
              item.workflow_phase || '',
              item.pending_reason || '',
              escapeField(item.blocked_by || ''),
              item.profile_section || '',
              item.essay_type || '',
              jsonToString(item.prerequisite_tasks),
              jsonToString(item.dependencies),
              jsonToString(item.tags),
              jsonToString(item.resource_urls),
              escapeField(item.module_name || ''),
              item.module_link || '',
              item.started_at || '',
              item.completion_date || item.completed_at || '',
              item.status_updated_at || '',
              escapeField(item.notes || ''),
              item.created_at || '',
              item.updated_at || ''
            ];
            csvRows.push(row.join(','));
          });

          data = csvRows.join('\n');
          filename = 'timeline_export.csv';
          mimeType = 'text/csv';
          break;

        case 'tasks':
          // Get user ID from profile data
          const tasksUserId = profileData?.academic?.user_id || profileData?.user_id || profileData?.id;
          if (!tasksUserId) {
            throw new Error('User ID not found in profile data');
          }

          // Fetch timeline items and filter for pending tasks
          const tasksResult = await timelineService.getTimelineItems(tasksUserId);
          if (tasksResult.error) {
            throw new Error('Failed to fetch timeline items: ' + tasksResult.error.message);
          }

          const allItems = tasksResult.data || [];
          const pendingTasks = allItems.filter(item =>
            ['planned', 'in_progress', 'rescheduled'].includes(item.status)
          );

          // Sort by date
          pendingTasks.sort((a, b) => {
            const dateA = new Date(a.scheduled_date || '9999-12-31');
            const dateB = new Date(b.scheduled_date || '9999-12-31');
            return dateA - dateB;
          });

          // Group by weeks and format
          const today = new Date();
          const currentWeekStart = new Date(today);
          currentWeekStart.setDate(today.getDate() - today.getDay()); // Start of current week

          const nextWeekStart = new Date(currentWeekStart);
          nextWeekStart.setDate(currentWeekStart.getDate() + 7);

          const thisWeek = [];
          const nextWeek = [];
          const upcoming = [];
          const critical = [];

          pendingTasks.forEach(task => {
            const taskDate = new Date(task.scheduled_date);
            const isPriorityCritical = task.priority === 'critical' || task.importance_level === 'critical';

            if (isPriorityCritical) {
              critical.push(task);
            } else if (taskDate >= currentWeekStart && taskDate < nextWeekStart) {
              thisWeek.push(task);
            } else if (taskDate >= nextWeekStart && taskDate < new Date(nextWeekStart.getTime() + 7 * 24 * 60 * 60 * 1000)) {
              nextWeek.push(task);
            } else {
              upcoming.push(task);
            }
          });

          // Format the checklist
          let checklist = `TO-DO LIST - Generated ${today.toLocaleDateString()}\n`;
          checklist += `================================\n\n`;

          if (critical.length > 0) {
            checklist += `CRITICAL DEADLINES:\n`;
            critical.slice(0, 10).forEach(task => {
              const date = new Date(task.scheduled_date).toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });
              const college = task.college?.name || task.college_name ? ` (${task.college?.name || task.college_name})` : '';
              checklist += `□ ${date} - ${task.title}${college}\n`;
            });
            checklist += `\n`;
          }

          if (thisWeek.length > 0) {
            checklist += `THIS WEEK:\n`;
            thisWeek.slice(0, 15).forEach(task => {
              const date = new Date(task.scheduled_date).toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });
              const college = task.college?.name || task.college_name ? ` (${task.college?.name || task.college_name})` : '';
              checklist += `□ ${date} - ${task.title}${college}\n`;
            });
            checklist += `\n`;
          }

          if (nextWeek.length > 0) {
            checklist += `NEXT WEEK:\n`;
            nextWeek.slice(0, 15).forEach(task => {
              const date = new Date(task.scheduled_date).toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });
              const college = task.college?.name || task.college_name ? ` (${task.college?.name || task.college_name})` : '';
              checklist += `□ ${date} - ${task.title}${college}\n`;
            });
            checklist += `\n`;
          }

          if (upcoming.length > 0) {
            checklist += `UPCOMING:\n`;
            upcoming.slice(0, 20).forEach(task => {
              const date = new Date(task.scheduled_date).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
              const college = task.college?.name || task.college_name ? ` (${task.college?.name || task.college_name})` : '';
              checklist += `□ ${date} - ${task.title}${college}\n`;
            });
            checklist += `\n`;
          }

          const completedCount = allItems.filter(item =>
            ['completed', 'done'].includes(item.status)
          ).length;
          checklist += `Progress: ${completedCount} completed, ${pendingTasks.length} remaining\n`;

          data = checklist;
          filename = 'task_checklist.txt';
          mimeType = 'text/plain';
          break;

        default:
          throw new Error('Unknown export format');
      }
      
      // Create and trigger download
      const blob = new Blob([data], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${filename} successfully`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleVerifyAllData = async () => {
    try {
      setVerifying(true);
      await onVerifyData();
      toast.success('Data verification complete');
    } catch (error) {
      console.error('Verification error:', error);
      toast.error('Failed to verify data');
    } finally {
      setVerifying(false);
    }
  };

  const handleRegenerateAI = async () => {
    try {
      setRegenerating(true);
      // This would trigger regeneration of all AI content
      await onVerifyData(); // For now, just refresh
      toast.success('AI content regeneration started');
    } catch (error) {
      console.error('Regeneration error:', error);
      toast.error('Failed to regenerate AI content');
    } finally {
      setRegenerating(false);
    }
  };

  const handleResetProfile = () => {
    if (window.confirm('Are you sure you want to reset your profile? This action cannot be undone.')) {
      toast.error('Profile reset is disabled for safety');
    }
  };

  const getConsistencyScore = () => {
    const totalFields = completeness?.totalFields || 100;
    const inconsistentFields = inconsistencies.length;
    return Math.max(0, Math.round(((totalFields - inconsistentFields) / totalFields) * 100));
  };

  const getLastVerification = () => {
    const lastUpdated = profileData?.academic?.updated_at;
    if (!lastUpdated) return 'Never';
    
    const daysSince = Math.floor((new Date() - new Date(lastUpdated)) / (1000 * 60 * 60 * 24));
    if (daysSince === 0) return 'Today';
    if (daysSince === 1) return 'Yesterday';
    if (daysSince < 7) return `${daysSince} days ago`;
    if (daysSince < 30) return `${Math.floor(daysSince / 7)} weeks ago`;
    return `${Math.floor(daysSince / 30)} months ago`;
  };

  const getAISummaryAge = () => {
    const lastUpdated = profileData?.academic?.ai_strategic_summary_updated_at;
    if (!lastUpdated) return 'Not generated';
    
    const daysSince = Math.floor((new Date() - new Date(lastUpdated)) / (1000 * 60 * 60 * 24));
    if (daysSince === 0) return 'Fresh (today)';
    if (daysSince === 1) return '1 day old';
    if (daysSince < 7) return `${daysSince} days old`;
    if (daysSince < 30) return `${Math.floor(daysSince / 7)} weeks old`;
    return 'Outdated';
  };

  return (
    <div className="space-y-4">
      {/* Export Your Data */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 sm:px-5 py-3 sm:py-4 bg-slate-900 text-white">
          <div className="flex items-center gap-3">
            <Download className="w-5 h-5" />
            <div>
              <h3 className="text-sm sm:text-base font-semibold mb-1">Export Your Data</h3>
              <div className="text-sm opacity-80">Complete profile in various formats</div>
            </div>
          </div>
        </div>
        
        <div className="p-4 sm:p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3">
            <button
              className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm disabled:opacity-50"
              onClick={() => handleExport('academic')}
              disabled={exporting}
            >
              <FileText className="w-4 h-4" />
              <span>Academic Profile (Text)</span>
            </button>
            
            <button
              className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm disabled:opacity-50"
              onClick={() => handleExport('assessment')}
              disabled={exporting || !profileData?.assessment?.responses}
            >
              <File className="w-4 h-4" />
              <span>Assessment Results</span>
            </button>
            
            <button
              className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm disabled:opacity-50"
              onClick={() => handleExport('colleges')}
              disabled={exporting || !profileData?.colleges?.length}
            >
              <FileSpreadsheet className="w-4 h-4" />
              <span>College List (CSV)</span>
            </button>
            
            <button
              className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm disabled:opacity-50"
              onClick={() => handleExport('complete')}
              disabled={exporting}
            >
              <FileJson className="w-4 h-4" />
              <span>Complete Profile (JSON)</span>
            </button>
            
            <button
              className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm disabled:opacity-50"
              onClick={() => handleExport('summary')}
              disabled={exporting || !profileData?.academic?.ai_strategic_summary}
            >
              <FileText className="w-4 h-4" />
              <span>Strategic Summary</span>
            </button>

            <button
              className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50"
              onClick={() => handleExport('timeline')}
              disabled={exporting}
            >
              <Calendar className="w-4 h-4" />
              <span>Timeline (CSV)</span>
            </button>

            <button
              className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm disabled:opacity-50"
              onClick={() => handleExport('tasks')}
              disabled={exporting}
            >
              <CheckSquare className="w-4 h-4" />
              <span>Task Checklist</span>
            </button>
          </div>
          
          {exporting && (
            <div className="mt-4 text-center text-sm text-slate-600">
              <RefreshCw className="w-4 h-4 animate-spin inline mr-2" />
              Preparing export...
            </div>
          )}
        </div>
      </div>

      {/* Profile Management */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 sm:px-5 py-3 sm:py-4 bg-slate-900 text-white">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5" />
            <div>
              <h3 className="text-sm sm:text-base font-semibold mb-1">Profile Management</h3>
              <div className="text-sm opacity-80">Data integrity and updates</div>
            </div>
          </div>
        </div>
        
        <div className="p-4 sm:p-5">
          <div className="space-y-3 mb-5">
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="font-medium text-slate-600 text-sm">Last Full Verification</span>
              <span className="text-slate-800 font-semibold text-sm">{getLastVerification()}</span>
            </div>
            
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="font-medium text-slate-600 text-sm">Data Consistency Score</span>
              <span className="text-slate-800 font-semibold text-sm">
                {getConsistencyScore()}% ({inconsistencies.length} inconsistencies)
              </span>
            </div>
            
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="font-medium text-slate-600 text-sm">AI Summary Freshness</span>
              <span className="text-slate-800 font-semibold text-sm">{getAISummaryAge()}</span>
            </div>
            
            <div className="flex justify-between items-center py-2">
              <span className="font-medium text-slate-600 text-sm">Profile Backup</span>
              <span className="text-slate-800 font-semibold text-sm">Auto-saved daily</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
            <button
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm disabled:opacity-50"
              onClick={handleVerifyAllData}
              disabled={verifying}
            >
              {verifying ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Shield className="w-4 h-4" />
              )}
              <span>Verify All Data</span>
            </button>
            
            <button
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-3 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors text-sm disabled:opacity-50"
              onClick={handleRegenerateAI}
              disabled={regenerating}
            >
              {regenerating ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              <span>Regenerate AI Content</span>
            </button>
            
            <button
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
              onClick={handleResetProfile}
            >
              <Database className="w-4 h-4" />
              <span>Reset Profile</span>
            </button>
          </div>
        </div>
      </div>

      {/* Data Quality Report */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 sm:px-5 py-3 sm:py-4 bg-slate-900 text-white">
          <div className="flex items-center gap-3">
            <Database className="w-5 h-5" />
            <h3 className="text-sm sm:text-base font-semibold">Data Quality Report</h3>
          </div>
        </div>
        
        <div className="p-4 sm:p-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <span className="text-xl sm:text-2xl font-bold text-slate-800 block">
                {completeness?.overall || 0}%
              </span>
              <span className="text-xs text-slate-500">Profile Complete</span>
            </div>
            
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <span className="text-xl sm:text-2xl font-bold text-slate-800 block">
                {completeness?.completedFields || 0}
              </span>
              <span className="text-xs text-slate-500">Fields Filled</span>
            </div>
            
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <span className="text-xl sm:text-2xl font-bold text-slate-800 block">
                {inconsistencies.filter(i => i.type === 'error').length}
              </span>
              <span className="text-xs text-slate-500">Critical Issues</span>
            </div>
            
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <span className="text-xl sm:text-2xl font-bold text-slate-800 block">
                {inconsistencies.filter(i => i.type === 'warning').length}
              </span>
              <span className="text-xs text-slate-500">Warnings</span>
            </div>
          </div>
          
          {inconsistencies.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="text-amber-800 font-semibold text-sm mb-2">Data Issues to Review:</h4>
              <ul className="space-y-1">
                {inconsistencies.slice(0, 3).map((issue, index) => (
                  <li key={index} className="text-amber-700 text-sm">
                    • {issue.message}
                  </li>
                ))}
                {inconsistencies.length > 3 && (
                  <li className="text-amber-700 text-sm">
                    • ...and {inconsistencies.length - 3} more issues
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExportManagementTab;